import { ButtonBase } from '@mui/material';
import { css, styled as muiStyled } from '@mui/material/styles';
import classnames from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { HeaderOpt } from './_type';
import useLeftNavContext from './left-nav-context';
import { OrganizationContextSwitcher } from '../../organization-context';
import { useAIResourcesContext } from '../../project/dashboard/ai-resources-context';

import Arranger from '~/client/components/base/arranger';
import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import { ArrowLeft } from '~/client/components/base/icon';
import { BrandLogo as AsenionLogo } from '~/client/components/base/icon/asenion-logo';
import { spc } from '~/client/components/base/spacer';
import Text from '~/client/components/base/text';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';
import { useOrganizationBranding } from '~/client/hooks/use-organization-branding';
import { useUserProfile } from '~/client/hooks/user-profile';
import { dashboardRouteUtil } from '~/client/routes';
import { getDisplayLogoUrl } from '~/client/services/organization-logo';
import * as GQL from '~/graphql/generated/client';
import { CircularProgress } from '~c/components/base/progress';

interface Props extends HeaderOpt {
  className?: string;
}

const COMPLIANCE_PATH = '/dashboard/compliance';

const LeftNavHeaderImpl: React.FC<Props> = ({
  className,
  showBackButton = false,
  showOrgSwitcher = false,
  renderText,
  onClick,
  loading,
  projectName,
  ...props
}) => {
  const navigate = useNavigate();
  const { state, goBack } = useLeftNavContext();
  const { navigateTo } = dashboardRouteUtil.useHelper();
  const { navColumnOpen, routeStack, currentRoute } = state;
  const { refetchAiSystemList, organizationUnitId, setOrganizationUnitId } =
    useAIResourcesContext();
  const { branding, organizationName } = useOrganizationBranding();
  const { userProfile } = useUserProfile();

  // Fetch user's organization contexts for the switcher
  const { data: contextData, loading: contextLoading } =
    GQL.useUserOrganizationContextsQuery({
      skip: !userProfile?.emailVerified,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    });

  // Always use Asenion logo
  const BrandIcon = AsenionLogo;

  // Show back button if requested
  const BackIcon = showBackButton ? ArrowLeft : undefined;

  if (loading) {
    return (
      <Arranger
        {...(!navColumnOpen && {
          justify: 'center',
          sx: { my: spc.container_padding_l },
        })}
        {...(navColumnOpen && {
          sx: { m: spc.container_padding_l },
        })}
      >
        <CircularProgress />
      </Arranger>
    );
  }

  const onBrandClick = () => {
    // Brand logo always navigates to compliance dashboard
    refetchAiSystemList();
    return navigateTo('COMPLIANCE');
  };

  const onBackClick = () => {
    // When a custom handler is provided, always prefer it — it knows the correct destination.
    if (onClick) {
      const prevRoute = routeStack[1];
      onClick(prevRoute);
      return;
    }
    // When back button is shown but no custom handler, navigate directly to compliance path.
    // Direct navigate avoids route-util params and startTransition, which can trigger
    // Suspense/hydration errors.
    if (showBackButton) {
      navigate(COMPLIANCE_PATH);
      return;
    }
    goBack();
  };

  let text: string | undefined;
  const prevRoute = routeStack[1];
  if (renderText && currentRoute) {
    text = renderText({
      current: currentRoute,
      prev: prevRoute,
      stack: routeStack,
    });
  }

  return (
    <div className={`${className} header-container`}>
      {/* Brand Logo - Only show when navColumnOpen */}
      {navColumnOpen && (
        <>
          <ButtonBase
            className={classnames(
              'v2-button brand-button',
              navColumnOpen ? 'v2-open' : 'v2-close'
            )}
            onClick={onBrandClick}
            {...props}
          >
            {branding?.logoUrl ? (
              <img
                src={getDisplayLogoUrl(branding.logoUrl)}
                alt={`${organizationName} Logo`}
                className="v2-icon brand-icon org-logo"
              />
            ) : (
              <BrandIcon className="v2-icon brand-icon" />
            )}
            {navColumnOpen && (
              <Text
                className="v2-text"
                sz="body12"
                noWrap
                align="left"
                color="inherit"
                sx={{
                  pl: spc.xxs,
                }}
              >
                {text}
              </Text>
            )}
          </ButtonBase>

          <div className="brand-spacer" />
        </>
      )}

      {/* Organization Context Switcher - Only visible in settings nav */}
      {showOrgSwitcher &&
        navColumnOpen &&
        userProfile?.defaultOrganizationId &&
        !(contextLoading && !contextData) && (
          <>
            <div className="org-switcher-container">
              <OrganizationContextSwitcher
                variant="button"
                data={contextData}
                showRoleLabels={false}
                isCollapsed={!navColumnOpen}
                selectedUnitId={organizationUnitId}
                onUnitSelect={setOrganizationUnitId}
              />
            </div>
            <div className="brand-separator" />
          </>
        )}

      {/* Back Button and Project Name Section */}
      {(showBackButton || projectName) && (
        <div
          className={classnames(
            'back-nav-section',
            navColumnOpen ? 'expanded' : 'minimized'
          )}
        >
          {showBackButton && BackIcon && (
            <ButtonBase
              type="button"
              className="back-button"
              onClick={onBackClick}
              {...props}
            >
              <BackIcon className="back-icon" />
            </ButtonBase>
          )}
          {navColumnOpen && projectName && (
            <Text
              className="project-name-text"
              sz="body14"
              noWrap
              align="left"
              color="inherit"
            >
              {projectName}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

const StyledLeftNavHeader = muiStyled(LeftNavHeaderImpl, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<Props & { tokens?: DesignTokenSet }>(({ theme, disabled, tokens }) => {
  const t = tokens ?? DesignToken;
  const hoverColor = theme.palette.primary.main;
  return css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 24px 0 0 0;
    min-height: 40px;
    overflow: visible;

    .v2-button {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      height: 50px;
      border-radius: ${t.radiusSmall}px;

      :hover {
        color: ${hoverColor};
        .v2-text > .MuiTypography-root {
          color: ${hoverColor};
        }
      }

      /* Brand logo styling */
      &.brand-button {
        margin-top: ${theme.spacing(spc.container_padding_l)};
        margin-left: 0;
        padding-left: ${theme.spacing(spc.container_padding_l)};
        overflow: visible;
        .v2-icon {
          font-size: ${disabled ? '36px' : '28px'};
          height: 40px;
          min-height: 40px;
          color: var(--org-primary-contrast, ${t.nav.itemTextOnDark});
          transition: all 0.3s ease;
          overflow: visible;
          display: flex;
          align-items: center;
        }

        /* When sidebar is open, show full logo */
        &.v2-open .v2-icon {
          width: 160px;
        }

        /* When sidebar is closed, show only icon */
        &.v2-close .v2-icon {
          width: 36px;
        }

        /* Organization logo (img) specific styling */
        .org-logo {
          max-width: 160px;
          max-height: 40px;
          width: auto;
          height: auto;
          object-fit: contain;
        }
      }
    }

    /* Back navigation section styling */
    .back-nav-section {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px ${theme.spacing(spc.s)};

      /* When navbar is minimized, add top margin to avoid overlapping toggle */
      &.minimized {
        margin-top: 60px;
        justify-content: center;
        padding: 8px 0;
      }

      /* Keep original styling when expanded */
      &.expanded {
        margin-top: 0;
      }
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      min-width: 28px;
      border-radius: ${t.radiusSmall}px;
      background-color: rgba(255, 255, 255, 0.12);
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }

      .back-icon {
        width: 16px;
        height: 16px;
        color: var(--org-primary-contrast, ${t.nav.itemTextOnDark});
      }
    }

    .project-name-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;

      .MuiTypography-root {
        color: var(--org-primary-contrast, ${t.nav.itemTextOnDark});
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .brand-spacer {
      height: ${theme.spacing(8)};
    }

    .brand-separator {
      width: 100%;
      height: 1px;
      background: rgba(255, 255, 255, 0.2);
      margin: 8px 0;
    }

    /* Organization context switcher styling */
    .org-switcher-container {
      padding: 10px 18px;
    }

    /* When back button is present, increase container height */
    &:has(.back-button) {
      min-height: 60px;
    }

    /* Open state styling */
    .v2-open.v2-button:not(.brand-button) {
      padding: ${disabled ? '0' : theme.spacing(spc.container_padding_m)};
      color: var(--org-primary-contrast, ${t.nav.itemTextOnDark});
    }

    /* Closed state styling */
    .v2-close.v2-button {
      justify-content: center;
    }
  `;
});

/**
 * Left Navigation Header
 *
 * A header component to be placed inside `<LeftNavDrawer/>`
 */
const LeftNavHeader: React.FC<Props> = (props) => {
  const t = useDesignTokens();
  return <StyledLeftNavHeader tokens={t} {...props} />;
};

export default LeftNavHeader;
